/**
 * Copyright 2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { MutableRefObject } from "react";
import { When } from "react-if";
import { v4 as uuidv4 } from "uuid";
import {
  Checkbox,
  FlexContainer,
  Spacer,
  Text,
  theme,
  Loader,
} from "@nordcloud/gnui";
import { noop } from "~/tools";
import { Separator, SidebarItemWrapper } from "../Styled";
import { FilterHeader } from "./FilterHeader";
import { useSelectableListFilter } from "./hooks";
import { SwitchWrap } from "./styles";
import { Item, Mode } from "./types";
import { getSelectedMode } from "./utils";

type Props = {
  allowModeChange?: boolean;
  isInfiniteScroll?: boolean;
  elementRef?: MutableRefObject<null>;
  isLoading?: boolean;
  selectableItems: Item[];
  selectedOptions: string[];
  onMultiSelect: (ids: string[]) => void;
  onModeChange?: (mode: Mode) => void;
  isExcludeSelected?: boolean;
};

export function SelectableListWithSearch({
  allowModeChange = false,
  isInfiniteScroll = false,
  isLoading = false,
  elementRef,
  selectedOptions = [],
  selectableItems,
  onMultiSelect,
  onModeChange = noop,
  isExcludeSelected = false,
}: Props) {
  const { selectedItems, filteredOptions, setSearch, onApply } =
    useSelectableListFilter({
      selectableItems,
      selectedOptions,
      onMultiSelect,
    });

  return (
    <>
      <FilterHeader
        showSearchBar
        allowModeChange={allowModeChange}
        defaultMode={getSelectedMode(isExcludeSelected)}
        onSetSearch={setSearch}
        onModeChange={onModeChange}
      />
      <Spacer height={theme.spacing.spacing04} />
      <FlexContainer
        direction="column"
        css={{ maxHeight: "10rem", overflowY: "scroll", overflowX: "hidden" }}
      >
        <Separator />
        {filteredOptions.map(({ label, value }) => (
          <SidebarItemWrapper
            key={uuidv4()}
            hideBottomBorder
            title={value ?? ""}
            onClick={() => onApply(value ?? "")}
          >
            <SwitchWrap>
              <Checkbox
                withoutLabel
                readOnly
                checked={selectedItems?.includes(value ?? "")}
              />
            </SwitchWrap>
            <Text marginBottom="0" css={{ textAlign: "start" }}>
              {label}
            </Text>
          </SidebarItemWrapper>
        ))}
        <When condition={isInfiniteScroll}>
          <When condition={isLoading}>
            <Loader inContent />
          </When>
          <div ref={elementRef} />
        </When>
      </FlexContainer>
    </>
  );
}
